import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import { ProductInfo } from "@redotech/redo-model/customer-event/customer-event-definition";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { memo, useCallback, useMemo, useState } from "react";
import { Flex } from "./flex";
import PackageIcon from "./icon-old/package.svg";
import { Pill, PillSize } from "./pill";
import * as productInfoCardButtonCss from "./product-info-card-button.module.css";
import { Text } from "./text";
import {
  formatProductInfoPriceRange,
  getProductUrl,
} from "./utils/general-utils";

export const ProductInfoCardButton = memo(function ProductInfoCardButton({
  product,
  storeUrl,
  openInNewTab,
  topRightHoveredIcon,
  readOnly = false,
  onClickUrlOverride,
}: {
  product: ProductInfo;
  storeUrl: string | undefined;
  openInNewTab: boolean;
  topRightHoveredIcon?: React.ReactNode | undefined;
  readOnly?: boolean;
  onClickUrlOverride?: string | undefined;
}) {
  const [imageUrl, setImageUrl] = useState<string | undefined>(
    product.imageUrl,
  );

  const onClickUrl = useMemo(() => {
    if (readOnly) {
      return undefined;
    } else if (onClickUrlOverride) {
      return onClickUrlOverride;
    } else if (storeUrl) {
      return getProductUrl(product, storeUrl);
    }
    return undefined;
  }, [product, storeUrl, readOnly, onClickUrlOverride]);

  const handleClick = useCallback(() => {
    if (!readOnly && onClickUrl) {
      window.open(onClickUrl, openInNewTab ? "_blank" : "_self");
    }
  }, [readOnly, onClickUrl, openInNewTab]);

  return (
    <Flex
      align="flex-start"
      className={productInfoCardButtonCss.productInfoCardButtonContainer}
      dir="row"
      gap="xl"
      justify="space-between"
      onClick={handleClick}
      p="xl"
      radius="sm"
    >
      <Flex
        className={productInfoCardButtonCss.overflowHidden}
        dir="row"
        gap="xl"
      >
        <Flex
          align="center"
          className={productInfoCardButtonCss.imgContainer}
          justify="center"
          radius="xs"
        >
          {imageUrl ? (
            <img
              alt={`${product.productTitle} image`}
              onError={() => setImageUrl(undefined)}
              src={imageUrl}
            />
          ) : (
            <PackageIcon />
          )}
        </Flex>
        <Flex
          align="flex-start"
          className={productInfoCardButtonCss.overflowHidden}
          dir="column"
          gap="xs"
          justify="center"
        >
          <Text
            className={productInfoCardButtonCss.productTitleText}
            fontSize="sm"
            overflow="hidden"
            textColor="primary"
            textOverflow="ellipsis"
          >
            {product.productTitle}
          </Text>
          <Pill size={PillSize.XSMALL} theme={PillTheme.NORMAL}>
            {formatProductInfoPriceRange(product)}
          </Pill>
        </Flex>
      </Flex>
      <Flex
        align="center"
        className={productInfoCardButtonCss.rightArrowContainer}
        dir="columnReverse"
        justify="space-between"
      >
        <ArrowRightIcon className={productInfoCardButtonCss.rightArrow} />
        {topRightHoveredIcon}
      </Flex>
    </Flex>
  );
});
